import { useEffect, useRef } from 'react';

/**
 * Listing footer that loads the next page when the user reaches the end of the current batch.
 * Parents append rows; this component only asks for the next page.
 */
const ListPagination = ({
  currentPage = 1,
  totalPages = 1,
  onNext,
  loading = false,
}) => {
  const sentinelRef = useRef(null);
  const onNextRef = useRef(onNext);
  const lockRef = useRef(false);
  const hasMore = Number(currentPage) < Number(totalPages);

  onNextRef.current = onNext;

  useEffect(() => {
    if (!loading) lockRef.current = false;
  }, [currentPage, loading]);

  useEffect(() => {
    if (!hasMore || loading || !onNextRef.current) return undefined;
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        if (lockRef.current) return;
        lockRef.current = true;
        onNextRef.current?.();
      },
      { root: null, rootMargin: '240px', threshold: 0 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, currentPage]);

  if (!hasMore && !loading) return null;

  return (
    <div ref={sentinelRef} className="flex justify-center py-4 text-sm text-gray-500 min-h-[1.5rem]">
      {loading ? 'Loading more…' : null}
    </div>
  );
};

export default ListPagination;
